import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Send, Camera, X, Loader2 } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';
import StarRating from '@/components/testimonials/StarRating';
import Turnstile from '@/components/Turnstile';
import { openRequest, submitRequest, compressPhoto } from '@/lib/testimonialRequests';

const input =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-orange-100 sm:text-sm';
const label = 'block text-sm font-bold text-slate-800';

/**
 * PUBLIC REVIEW PAGE reached from an admin "Request Testimonial" email/SMS.
 * The one-time token is the access control — no account needed. Spam
 * protection: unguessable token (one submission), hidden honeypot field,
 * minimum fill time, Turnstile (verified server-side) and link limits.
 */
const ReviewRequest: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { lang, path } = useI18n();
  const es = lang === 'es';
  const started = useRef(Date.now());

  const [state, setState] = useState<'loading' | 'ready' | 'invalid' | 'done' | 'already'>('loading');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [city, setCity] = useState('');
  const [st, setSt] = useState('');
  const [customerType, setCustomerType] = useState<'contractor' | 'homeowner'>('contractor');
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState('');
  const [recommend, setRecommend] = useState(true);
  const [permission, setPermission] = useState(false);
  const [photo, setPhoto] = useState('');
  const [website, setWebsite] = useState('');
  const [tsToken, setTsToken] = useState('');
  const [tsReset, setTsReset] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    openRequest(token).then(({ data, error: e }) => {
      if (!alive) return;
      if (e || !data?.ok) return setState('invalid');
      if (data.already_submitted) return setState('already');
      const p = data.prefill || {};
      setName(p.name || '');
      setCompany(p.company || '');
      setCity(p.city || '');
      setSt(p.state || '');
      setCustomerType(p.customerType === 'homeowner' ? 'homeowner' : 'contractor');
      setState('ready');
    });
    return () => {
      alive = false;
    };
  }, [token]);

  const onPhoto = async (f?: File | null) => {
    if (!f) return;
    try {
      setPhoto(await compressPhoto(f));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (name.trim().length < 2 || city.trim().length < 2) return setError(es ? 'Ingrese su nombre y ciudad.' : 'Please enter your name and city.');
    if (body.trim().length < 10) return setError(es ? 'Escriba al menos 10 caracteres.' : 'Please write at least 10 characters.');
    if (!permission) return setError(es ? 'Marque la casilla de permiso para publicar.' : 'Please tick the permission box so we can publish it.');
    setSaving(true);
    const { data, error: err } = await submitRequest({
      token, rating, body, name, company, city, state: st, customerType, recommend, permission, lang,
      photo: photo || undefined, website, elapsedMs: Date.now() - started.current, turnstileToken: tsToken,
    });
    setSaving(false);
    setTsReset((n) => n + 1);
    if (err || !data?.ok) {
      if (String(err).includes('already')) return setState('already');
      return setError(es ? 'No pudimos guardar su reseña. Intente de nuevo.' : `We couldn't save your review (${err}). Please try again.`);
    }
    setState('done');
  };

  const shell = (children: React.ReactNode) => (
    <div className="min-h-[70vh] bg-brand-canvas px-4 py-10 sm:py-16">
      <Seo title={es ? 'Deje su reseña | GigGone' : 'Leave your review | GigGone'} description="GigGone review" noindex />
      <div className="mx-auto max-w-2xl">{children}</div>
    </div>
  );

  if (state === 'loading')
    return shell(<p className="flex items-center justify-center gap-2 py-20 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> {es ? 'Cargando…' : 'Loading…'}</p>);

  if (state === 'invalid' || state === 'already' || state === 'done') {
    const ok = state !== 'invalid';
    return shell(
      <div className={`rounded-2xl border p-8 text-center ${ok ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
        {ok ? <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" /> : <AlertCircle className="mx-auto h-12 w-12 text-slate-400" />}
        <h1 className="mt-4 text-2xl font-extrabold text-slate-900">
          {state === 'done' ? (es ? '¡Gracias por su reseña!' : 'Thank you for your review!') : state === 'already' ? (es ? 'Ya recibimos su reseña' : 'We already have your review') : (es ? 'Este enlace no es válido' : 'This link is not valid')}
        </h1>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
          {state === 'invalid'
            ? es ? 'Puede dejar una reseña desde nuestra página pública.' : 'You can still leave a review from our public reviews page.'
            : es ? 'Nuestro equipo la revisará antes de publicarla.' : 'Our team reviews every submission before it is published.'}
        </p>
        <Link to={state === 'invalid' ? `${path('/testimonials')}#leave-a-review` : path('/testimonials')} className="mt-6 inline-flex rounded-xl bg-brand-orange px-6 py-3 text-sm font-bold text-white hover:bg-orange-600">
          {es ? 'Ver reseñas' : 'See reviews'}
        </Link>
      </div>
    );
  }

  return shell(
    <form onSubmit={submit} noValidate className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card sm:p-8">
      <h1 className="text-2xl font-extrabold text-slate-900">{es ? '¿Cómo fue su experiencia?' : 'How was your experience?'}</h1>
      <p className="mt-1 text-sm text-slate-500">{es ? 'Toma menos de un minuto.' : 'Takes less than a minute.'}</p>

      {/* Honeypot — hidden from people, filled by bots */}
      <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} className="absolute left-[-9999px] h-0 w-0 opacity-0" aria-hidden="true" name="website" />

      <div className="mt-6">
        <span className={label}>{es ? 'Calificación' : 'Rating'} *</span>
        <div className="mt-2"><StarRating value={rating} onChange={setRating} size="lg" /></div>
      </div>

      <div className="mt-5">
        <label className={label} htmlFor="rv-body">{es ? 'Su reseña' : 'Your review'} *</label>
        <textarea id="rv-body" rows={5} maxLength={2000} value={body} onChange={(e) => setBody(e.target.value)} className={`mt-1.5 ${input}`} placeholder={es ? '¿Qué le gustó de GigGone?' : 'What did you like about GigGone?'} />
        <p className="mt-1 text-right text-xs text-slate-400">{body.length}/2000</p>
      </div>

      <div className="mt-2 grid gap-4 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor="rv-name">{es ? 'Nombre' : 'Name'} *</label>
          <input id="rv-name" value={name} onChange={(e) => setName(e.target.value)} className={`mt-1.5 ${input}`} autoComplete="name" />
          <p className="mt-1 text-xs text-slate-500">{es ? 'Publicamos solo nombre e inicial.' : 'We only publish first name + last initial.'}</p>
        </div>
        <div>
          <label className={label} htmlFor="rv-company">{es ? 'Empresa' : 'Company name'}</label>
          <input id="rv-company" value={company} onChange={(e) => setCompany(e.target.value)} className={`mt-1.5 ${input}`} autoComplete="organization" />
        </div>
        <div>
          <label className={label} htmlFor="rv-city">{es ? 'Ciudad' : 'City'} *</label>
          <input id="rv-city" value={city} onChange={(e) => setCity(e.target.value)} className={`mt-1.5 ${input}`} autoComplete="address-level2" />
        </div>
        <div>
          <label className={label} htmlFor="rv-state">{es ? 'Estado' : 'State'}</label>
          <input id="rv-state" value={st} onChange={(e) => setSt(e.target.value.slice(0, 20))} className={`mt-1.5 ${input}`} autoComplete="address-level1" />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {(['contractor', 'homeowner'] as const).map((v) => (
          <button key={v} type="button" aria-pressed={customerType === v} onClick={() => setCustomerType(v)}
            className={`rounded-lg border px-4 py-2.5 text-sm font-bold ${customerType === v ? 'border-brand-navy bg-brand-navy text-white' : 'border-slate-300 text-slate-700'}`}>
            {v === 'contractor' ? (es ? 'Contratista' : 'Contractor') : (es ? 'Propietario' : 'Homeowner')}
          </button>
        ))}
        <button type="button" aria-pressed={recommend} onClick={() => setRecommend(!recommend)}
          className={`rounded-lg border px-4 py-2.5 text-sm font-bold ${recommend ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-300 text-slate-700'}`}>
          {es ? 'Recomiendo GigGone' : 'I recommend GigGone'}
        </button>
      </div>

      <div className="mt-5">
        <span className={label}>{es ? 'Foto (opcional)' : 'Photo (optional)'}</span>
        {photo ? (
          <div className="relative mt-2 inline-block">
            <img src={photo} alt="" className="h-28 w-28 rounded-xl object-cover" />
            <button type="button" onClick={() => setPhoto('')} className="absolute -right-2 -top-2 rounded-full bg-slate-900 p-1 text-white" aria-label="Remove photo"><X className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <label className="mt-2 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            <Camera className="h-4 w-4" /> {es ? 'Agregar foto del trabajo o de usted' : 'Add a photo of your work or yourself'}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => onPhoto(e.target.files?.[0])} />
          </label>
        )}
      </div>

      <label className="mt-5 flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <input type="checkbox" checked={permission} onChange={(e) => setPermission(e.target.checked)} className="mt-0.5 h-5 w-5" />
        <span className="text-sm text-slate-700">{es ? 'Doy permiso a GigGone para publicar esta reseña (nombre e inicial, ciudad, empresa y foto).' : 'I give GigGone permission to publish this review (first name + initial, city, company and photo).'}</span>
      </label>

      <Turnstile action="testimonial_request" onVerify={setTsToken} resetKey={tsReset} className="mt-4" />

      {error && <p className="mt-4 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2.5 text-sm font-semibold text-red-700"><AlertCircle className="h-4 w-4" /> {error}</p>}

      <button type="submit" disabled={saving} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-orange px-6 py-3.5 text-base font-bold text-white hover:bg-orange-600 disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {saving ? (es ? 'Enviando…' : 'Sending…') : es ? 'Enviar reseña' : 'Submit review'}
      </button>
    </form>
  );
};

export default ReviewRequest;
